/**
 * @author Красильников Даниил <dkrasilnikov@gmail.com>
 * @link https://t.me/krasilneg
 * 
 * @author Колесник Данил <90.kalias.3@gmail.com>
 * @link https://t.me/kalias_90
 */

import React, { CSSProperties, PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import Close from '@mui/icons-material/Close';
import Search from '@mui/icons-material/Search';
import { Window, WindowsContextProvider } from '../../windows';
import ElementProperties from './ElementProperties.js';
import { ProjectData } from '@/state/types/project-data';
import { ElementDetails, ViewerControllerInterface } from '@bimdata/viewer-react';
import ViewerSettings from './ViewerSettings.js';
import TextField from '@mui/material/TextField';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import Skeleton from '@mui/material/Skeleton';
import hinted from '@/components/Hinted.js';
import Unpropagatable from '@/components/Unpropagatable.js';
import t from '@/utils/translate';
import { WindowBind } from '@/components/windows/window.controller.js';

const WindowContent = styled('div')({
  background: '#FFFFFF',
  border: '1px solid #C1C7D0',
  boxShadow: '0px 0px 10px 0px #00000014',
  borderRadius: '8px',
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  height: '100%',
});

const Header = styled('div')({
  height: '46px',
  padding: '10px 10px 0 10px',
  fontStyle: 'normal',
  fontWeight: '400',
  fontSize: '14px',
  lineHeight: '16px',
  color: '#091E42',
  userSelect: 'none',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '10px',
});

const HeaderContent = styled('div')({
  display: 'flex',
  flexWrap: 'nowrap',
  alignItems: 'center',
  justifyContent: 'start',
  flexGrow: 1,
  gap: '10px',
  height: '36px',
});

const HeaderTitle = styled('span')({
  paddingLeft: '4px',
});

const HeaderButtons = styled(Unpropagatable)({
  flexGrow: 0,
  display: 'flex',
  alignItems: 'center',
  gap: '2px',
  height: '36px',
});

const ElementId = hinted(
  styled('span')({
    fontSize: '13px',
    padding: '0px 7px',
    border: '1px solid #C1C7D0',
    borderRadius: '4px',
    cursor: 'pointer',
    userSelect: 'none',
    height: '24px',
    lineHeight: '24px',
  }),
  {
    placement: 'bottom-start',
    delay: 250,
    fadeDuration: 50,
    hint: t('action.copy')
  }
);

const CopyableElementId = ({ children }: PropsWithChildren) => {
  const [ isCopied, setIsCopied ] = useState(false);

  return <ElementId
    hint={isCopied ? t('state.copied') : t('action.copy') }
    onClick={(e) => {
      navigator.clipboard.writeText((e.target as HTMLSpanElement).innerText);
      setIsCopied(true);
    }}
    onMouseLeave={(e) => {
      setIsCopied(false);
    }}
  >
    {children}
  </ElementId>
};

const WindowHeaderButton = styled('button')(({ activable = false }: { activable?: boolean }) => ({
  height: '32px',
  width: '32px',
  borderRadius: '6px',
  padding: '4px',
  background: 'transparent',
  border: 'none',
  color: '#6B778C',
  cursor: 'pointer',
  '&:hover': {
    color: '#0747A6',
  },
  ...(activable
    ? {
      '&:active, &.active': {
        boxShadow: '1px 1px 2px 0px #6B778C40 inset',
        background: '#FAFAFA',
        color: '#0747A6',
      }
    }
    : {}
  )
}));

const CloseIcon = styled(Close)({
  fontSize: 24,
});

const SearchIcon = styled(Search)({
  fontSize: 22,
});

const SearchField = styled(TextField)({
  width: '100%',

  '& .MuiInputBase-root': {
    paddingRight: '10px',

    '& .MuiInputBase-input': {
      padding: '6px 10px 6px 10px',
      fontSize: '14px',
      color: '#091E42',
  
      '&::placeholder': {
        color: '#C1C7D0',
      }
    },
  
    '& .MuiSvgIcon-root': {
      marginLeft: '10px',
      fontSize: '18px',
      color: '#C1C7D0',
    },
  
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#C1C7D0',
    },

    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#0052CC',
      borderWidth: '1px',
    }
  },
});

type Props = {
  detailsOpen: boolean,
  elementDetails: ElementDetails | null,
  selectionLength: number,
  onCloseDetails: () => void,
  settingsOpen: boolean,
  viewer: ViewerControllerInterface,
  onCloseSettings: () => void,
  style?: CSSProperties,
  minLayer?: number
};

export default ({
  detailsOpen, elementDetails, selectionLength, onCloseDetails,
  settingsOpen, viewer, onCloseSettings, style, minLayer
}: Props) => {
  const searchField = useRef<HTMLDivElement>(null);

  const [filterActive, setFilterActive] = useState<boolean>(false);
  const [filter, setFilter] = useState<string>('');
  
  useEffect(() => {
    if (filterActive && searchField.current) {
      searchField.current.querySelector('input')?.focus();
    }
  }, [filterActive, searchField.current]);

  const toggleFilter = useCallback(() => {
    setFilter('');
    setFilterActive(!filterActive);
  }, [ filterActive ]);

  return (
    <WindowsContextProvider style={style} minLayer={minLayer}>
      <Window
        header="#elementDetailsHeader"
        opened={detailsOpen}
        bind={WindowBind.BOTTOM_RIGHT}
        initialHeight={500}
        initialWidth={400}
        minHeight={300}
        minWidth={380}
      >
        <WindowContent>
          <Header id='elementDetailsHeader'>
            <HeaderContent>
              {!!filterActive && (
                <Unpropagatable events={[ 'mousedown' ]} style={{ width: '100%' }}>
                  <SearchField
                    ref={searchField}
                    placeholder={t('action.search')}
                    variant="outlined"
                    InputProps={
                      {
                        ...(!filter ? {} : {
                          endAdornment: (
                            <ClearOutlinedIcon sx={{ cursor: 'pointer' }} onClick={() => setFilter('')}/>
                          )
                        })
                      }
                    }
                    value={filter}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setFilter(event.target.value);
                    }}
                    onKeyDown={(e) => e.stopPropagation()}
                  />
                </Unpropagatable>
              )}
              {!filterActive && (
                  <>
                    <HeaderTitle>{t('viewer.title.elementProps', true)}</HeaderTitle>
                    {
                      elementDetails
                        ? (<Unpropagatable events={[ 'mousedown' ]}>
                          <CopyableElementId>
                            {
                              elementDetails.attributes?.find(({ code }) => code === 'PG_IDENTITY_DATA')?.attributes.
                              find(({ code }) => code === 'REVIT_ID')?.value || elementDetails.id
                            }
                          </CopyableElementId>
                        </Unpropagatable>)
                        : (selectionLength === 1 && <Skeleton variant="rounded" width={60} height={16} style={{ display: 'inline-block', marginLeft: '7px' }} />)
                    }
                  </>
                )
            }
            </HeaderContent>
            <HeaderButtons events={[ 'mousedown' ]}>
              {(filterActive || !!elementDetails) && (
                <WindowHeaderButton className={filterActive ? 'active' : ''} activable onClick={toggleFilter}>
                  <SearchIcon viewBox='0 -1 24 24'/>
                </WindowHeaderButton>
              )}
              <WindowHeaderButton onClick={onCloseDetails}>
                <CloseIcon/>
              </WindowHeaderButton>
            </HeaderButtons>
          </Header>
          <ElementProperties
            filter={filterActive ? filter : null}
            element={elementDetails}
            selectionLength={selectionLength}
            onClose={onCloseDetails}
          />
        </WindowContent>
      </Window>

      <Window
        header="#viewerSettingsHeader"
        opened={settingsOpen}
        bind={WindowBind.BOTTOM_RIGHT}
        initialHeight={500}
        initialWidth={400}
        minHeight={300}
        minWidth={380}
      >
        <WindowContent>
          <Header id='viewerSettingsHeader'>
            <HeaderTitle>{t('viewer.title.settings', true)}</HeaderTitle>
            <HeaderButtons events={[ 'mousedown' ]}>
              <WindowHeaderButton onClick={onCloseSettings}>
                <CloseIcon/>
              </WindowHeaderButton>
            </HeaderButtons>
          </Header>
          <ViewerSettings viewer={viewer}/>
        </WindowContent>
      </Window>
    </WindowsContextProvider>
  );
};